/**
 * Component that handles the multiple Save buttons in the composer.
 */
import Button from '@bufferapp/ui/Button'
import FlashIcon from '@bufferapp/ui/Icon/Icons/Flash'
import dayjs from 'dayjs'
import dayjsTimezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import styled from 'styled-components'

import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import {
  useAccount,
  useOneBufferBilling,
} from '~publish/contexts/AccountContext'
import { selectContentGenerationStatus } from '~publish/legacy/ai/state/slice'
import {
  NotificationScopes,
  SaveButtonTypes,
} from '~publish/legacy/composer/composer/AppConstants'
import ModalActionCreators from '~publish/legacy/composer/composer/shared-components/modal/actionCreators'
import type {
  ComposerQueueingType,
  ComposerSaveButton,
} from '~publish/legacy/composer/composer/stores/types'
import { getThreadedUpdatesUpgradePath } from '~publish/legacy/composer/composer/utils/upgradePaths'
import { AppEnvironments } from '~publish/legacy/constants'
import { ConfirmationCoachMark } from '~publish/legacy/reminders/components/new-reminders/components/confirmation-coachmark/ConfirmationCoachmark'
import { useAppSelector } from '~publish/legacy/store'

import ComposerStore from '../../stores/ComposerStore'
import { ConditionalTooltip } from '../ConditionalTooltip'
import NotificationContainer from '../NotificationContainer'
import {
  addedToQueueCopyMap,
  addingToQueueCopyMap,
  getDraftButtons,
  getSaveButton,
  getSaveButtonsCopy,
} from './action-buttons'
import { DraftSaveButtons } from './DraftSaveButtons'
import { useBeforeFirstAction } from './hooks/useBeforeFirstAction'
import { InlineScheduler } from './InlineScheduler'
import { OmniboxButtons } from './OmniboxButtons'
import { StackedSaveButtons } from './StackedSaveButtons'
import type { UpdateSaverProps } from './types'
import { UpdateSaverForApprovals } from './UpdateSaverForApprovals'
import UpdateSaverItem from './UpdateSaverItem'
import { getFormattedWhatPreventsSavingMessages } from './utils'
import type { Notification } from '../../values/Notification'
import styles from '../css/UpdateSaver.module.css'

dayjs.extend(utc)
dayjs.extend(dayjsTimezone)

const ActionButtons = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
`

const shouldHidePublishButtons = ({
  visibleNotifications,
}: {
  visibleNotifications: Notification[]
}): boolean => {
  const pendingDrafts = ComposerStore.getEnabledDrafts().filter(
    (draft) => !draft.isSaved,
  )

  // If no pending drafts, no need to hide buttons
  if (pendingDrafts.length === 0) return false

  const notificationScopes = new Set(
    visibleNotifications
      .filter((notification) =>
        notification.scope.startsWith(NotificationScopes.PROFILE_QUEUE_LIMIT),
      )
      .map((notification) => notification.scope),
  )

  const allPendingReachedLimit = pendingDrafts.every((draft) =>
    notificationScopes.has(
      `${NotificationScopes.PROFILE_QUEUE_LIMIT}-${draft.service.name}`,
    ),
  )
  return allPendingReachedLimit
}

const UpdateSaver = (props: UpdateSaverProps): JSX.Element | null => {
  const {
    appState,
    userData,
    metaData,
    visibleNotifications,
    timezone,
    moreThanOneProfileSelected,
    areAllDraftsSaved,
    saveButtons,
    isSlotPickingAvailable,
    availableSchedulesSlotsForDay = [],
    isPinnedToSlot,
    selectedProfiles,
    sentPost,
    draftMode,
    whatPreventsSavingMessages: preventsSavingMessages = [],
    whatPreventsSavingDraftMessages = [],
    shouldShowThreadsMigrationModal,
    requiresApprovalForSelectedProfiles,
    threadsMigrationModalService = 'omni',
    draftPreview,
  } = props
  let { scheduledAt } = props

  const user = useAccount()
  const billing = useOneBufferBilling()

  const commonTrackingProps =
    user?.account?.currentOrganization?.commonTrackingProperties || null

  const { contentGenerationInProgress } = useAppSelector((state) =>
    selectContentGenerationStatus(state),
  )

  const {
    isSavingPossible,
    isSavingDraftPossible,
    isDraftsSavePending,
    draftSaveQueueingType,
    isOmniboxEnabled,
  } = appState

  const doSelectedProfilesHaveSlots = selectedProfiles.some(
    (profile): boolean => profile.profileHasPostingSchedule,
  )
  const beforeFirstAction = useBeforeFirstAction({ draftPreview })

  const hidePublishButtons = shouldHidePublishButtons({
    visibleNotifications,
  })

  if (saveButtons.length === 0) return null

  const { weekStartsMonday } = userData

  const inlineButtonsWrapperClassName = [
    isDraftsSavePending
      ? styles.savePendingInlineButtonsWrapper
      : styles.inlineButtonsWrapper,
  ].join(' ')

  const stackedButtonsWrapperClassName = [
    isDraftsSavePending
      ? styles.savePendingStackedButtonsWrapper
      : styles.stackedButtonsWrapper,
  ].join(' ')

  const notificationContainerClassNames = {
    container: styles.notificationContainer,
    notification: styles.notification,
  }

  const saveButtonsCopy = getSaveButtonsCopy({
    moreThanOneProfileSelected,
    draftMode,
  })

  const getActiveSaveButtonCopy = (
    buttonType: ComposerSaveButton,
    queueingType: ComposerQueueingType,
  ): string | Array<string> | null => {
    let finalButtonCopy: string | Array<string> | undefined
    if (isDraftsSavePending)
      finalButtonCopy = addingToQueueCopyMap.get(queueingType)
    else if (areAllDraftsSaved)
      finalButtonCopy = addedToQueueCopyMap.get(queueingType)
    else finalButtonCopy = saveButtonsCopy.get(buttonType)

    return finalButtonCopy ?? null
  }

  // Determine whether we should render the inline scheduler
  // If so, we need to format the scheduledAt date
  if (sentPost) scheduledAt = null
  const shouldDisplayInlineScheduler = scheduledAt !== null

  // if the user doesn't have any slots available, then they will
  // only see these two buttons in the composer.
  const shouldLimitButtonOptions =
    saveButtons[0] !== SaveButtonTypes.ADD_TO_DRAFTS &&
    saveButtons[0] !== SaveButtonTypes.SAVE &&
    !doSelectedProfilesHaveSlots &&
    !shouldDisplayInlineScheduler

  const buttons = shouldLimitButtonOptions
    ? [SaveButtonTypes.SHARE_NOW, SaveButtonTypes.SCHEDULE_POST]
    : saveButtons

  const [inlineSaveButtonTypes, stackedSaveButtonTypes] = getSaveButton(buttons)

  const isEditPost = buttons && buttons[0] === SaveButtonTypes.SAVE
  const displayThreadsPaywallButtons =
    shouldShowThreadsMigrationModal && !isEditPost && !draftMode

  const showOmniboxButtons = isOmniboxEnabled
  const showAddToDrafts = !showOmniboxButtons && !draftMode && !isEditPost

  const { isOneBufferOrganization } = user?.account?.currentOrganization || {}
  const planId = billing?.subscription?.plan?.id
  const planSupportsApprovals =
    planId !== 'essentials' && planId !== 'free' && isOneBufferOrganization

  const showApprovalButtons =
    planSupportsApprovals &&
    requiresApprovalForSelectedProfiles !== 'none' &&
    // for now Save and Request Approval is not available when editing an existing draft
    !draftMode &&
    !showOmniboxButtons &&
    !displayThreadsPaywallButtons

  const displayInlineSaveButtons =
    !isOmniboxEnabled &&
    inlineSaveButtonTypes.length > 0 &&
    !displayThreadsPaywallButtons &&
    !showApprovalButtons

  const displayStackedSaveButtons =
    !isOmniboxEnabled &&
    stackedSaveButtonTypes.length > 0 &&
    !displayThreadsPaywallButtons &&
    !showApprovalButtons &&
    !hidePublishButtons

  const [firstStackedButtonType, ...otherStackedButtonsTypes] =
    stackedSaveButtonTypes

  const firstStackedButtonCopy = getActiveSaveButtonCopy(
    firstStackedButtonType,
    draftSaveQueueingType,
  )

  const shouldDisplayTime =
    (isEditPost && !doSelectedProfilesHaveSlots) || draftMode

  const isDraftSaveDisabled = !isSavingDraftPossible
  const isPostSaveDisabled = !isSavingPossible
  const isSaveDisabled =
    (draftMode ? isDraftSaveDisabled : isPostSaveDisabled) ||
    contentGenerationInProgress

  const hasInitialDate = metaData.emptySlotMode || shouldDisplayInlineScheduler
  const warningMessages = draftMode
    ? whatPreventsSavingDraftMessages
    : preventsSavingMessages
  const whatPreventsSavingMessages = getFormattedWhatPreventsSavingMessages({
    warningMessages,
  })

  const threadedUpdatesUpgradePath = getThreadedUpdatesUpgradePath(
    threadsMigrationModalService,
  )

  if (showApprovalButtons) {
    return <UpdateSaverForApprovals {...props} />
  }

  return (
    <section className={styles.section} data-testid="update-saver-buttons">
      {(shouldDisplayInlineScheduler || shouldDisplayTime) && (
        <InlineScheduler
          scheduledAt={scheduledAt}
          timezone={timezone}
          userData={userData}
          metaData={metaData}
          doSelectedProfilesHaveSlots={doSelectedProfilesHaveSlots}
          draftMode={draftMode}
          isPinnedToSlot={isPinnedToSlot}
          shouldDisplayTime={shouldDisplayTime}
          isSlotPickingAvailable={isSlotPickingAvailable}
          availableSchedulesSlotsForDay={availableSchedulesSlotsForDay}
        />
      )}

      <ActionButtons>
        {showOmniboxButtons && <OmniboxButtons data-testid="omnibox-buttons" />}
        {showAddToDrafts && (
          <ConditionalTooltip
            content={isDraftSaveDisabled ? whatPreventsSavingMessages : null}
          >
            <div
              data-testid="draft-save-buttons-section"
              className={inlineButtonsWrapperClassName}
              data-disabled={isDraftSaveDisabled}
            >
              <DraftSaveButtons
                isDisabled={isDraftSaveDisabled}
                timezone={timezone}
                draftSaveItems={getDraftButtons({
                  hasInitialDate,
                  showApprovalPaywall: !planSupportsApprovals,
                })}
              />
            </div>
          </ConditionalTooltip>
        )}

        {displayInlineSaveButtons && (
          <ConditionalTooltip
            content={isSaveDisabled ? whatPreventsSavingMessages : null}
          >
            <div
              data-testid="inline-save-buttons"
              className={inlineButtonsWrapperClassName}
              data-disabled={isSaveDisabled}
            >
              {inlineSaveButtonTypes.map((saveButtonType, i) => (
                <UpdateSaverItem
                  key={saveButtonType}
                  type={saveButtonType}
                  draftMode={draftMode}
                  userData={userData}
                  timezone={timezone}
                  weekStartsMonday={weekStartsMonday}
                  isInlineSchedulerDisplayed={shouldDisplayInlineScheduler}
                  isSecondaryItem={i < inlineSaveButtonTypes.length - 1}
                >
                  {draftSaveQueueingType === saveButtonType
                    ? getActiveSaveButtonCopy(
                        saveButtonType,
                        draftSaveQueueingType,
                      )
                    : saveButtonsCopy.get(saveButtonType) ?? null}
                </UpdateSaverItem>
              ))}
            </div>
          </ConditionalTooltip>
        )}

        {displayStackedSaveButtons && (
          <ConditionalTooltip content={whatPreventsSavingMessages}>
            <div
              data-testid="stacked-save-buttons-section"
              className={stackedButtonsWrapperClassName}
              data-disabled={isSaveDisabled}
            >
              <ConfirmationCoachMark selectedProfiles={selectedProfiles}>
                <div>
                  <StackedSaveButtons
                    isSavingPossible={isSavingPossible}
                    isSavingDraftPossible={isSavingDraftPossible}
                    draftMode={draftMode}
                    timezone={timezone}
                    firstStackedButtonLabel={firstStackedButtonCopy ?? ''}
                    firstStackedButtonType={firstStackedButtonType}
                    otherStackedButtonsTypes={otherStackedButtonsTypes}
                    beforeFirstAction={beforeFirstAction}
                    saveButtonsCopy={saveButtonsCopy}
                  />
                </div>
              </ConfirmationCoachMark>
            </div>
          </ConditionalTooltip>
        )}

        {displayThreadsPaywallButtons && (
          <Button
            data-testid="threads-free-paywall-button"
            icon={<FlashIcon color="white" />}
            type="primary"
            label="Upgrade to Schedule Thread"
            onClick={(): void => {
              BufferTracker.cTAClicked({
                organizationId: user?.account?.currentOrganization?.id || '',
                cta: `publish-composer-createPost-upgradeToScheduleThread-1`,
                upgradePathName: threadedUpdatesUpgradePath,
                product: 'publish',
                clientName: 'publishWeb',
                ...commonTrackingProps,
              })
              ModalActionCreators.openModal('ThreadsFreeOBPaywall', {
                ctaString: 'composer-upgradeButton-upgradeToScheduleThread-1',
                service: threadsMigrationModalService,
              })
            }}
          />
        )}
      </ActionButtons>

      {metaData.appEnvironment === AppEnvironments.EXTENSION && (
        <NotificationContainer
          visibleNotifications={visibleNotifications}
          scope={NotificationScopes.UPDATE_SAVING_AGGREGATE}
          classNames={notificationContainerClassNames}
        />
      )}
    </section>
  )
}

export default UpdateSaver
